import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  IconButton,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { ArrowBack, Edit, Delete } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import AddButton from "../components/AddButton";
import ResultCount from "../components/ResultCount";
import ClearFiltersButton from "../components/ClearFiltersButton";
import { filterFieldSx } from "../theme/filterStyles";
import StandardPagination from "../components/Pagination/StandardPagination";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import { Toast } from "../components/Toast";
import ConfirmModal from "../components/modals/ConfirmModal";
import ProfessionalServiceValueModal from "../components/modals/ProfessionalServiceValueModal";
import { serviceTypeService, serviceTypeLabel } from "../services/serviceTypeService";
import {
  professionalServiceValueService,
  ProfessionalServiceValueItem,
} from "../services/professionalServiceValueService";
import { colors, pageHeader } from "../theme/designSystem";

interface Option {
  id: string;
  name: string;
}

const fmtMoney = (n?: number) =>
  `R$ ${(n ?? 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const BLOCKED_ACTIVATION_MESSAGE =
  "Já existe um registro ativo deste serviço nesta equipe — inative-o antes de reativar este.";

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString("pt-BR") : "-";

const ProfessionalServiceValues: React.FC = () => {
  const { navigateTo, getAllParams } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const params = getAllParams();
  const professionalId = params.get("professionalId") || "";
  const professionalName = params.get("professionalName") || "Profissional";
  const especialidade = params.get("especialidade") || "";
  const professionalTypeId = params.get("professionalTypeId") || "";
  const entityId = params.get("entityId") || "";
  const entityName = params.get("entityName") || "Entidade";

  const [items, setItems] = useState<ProfessionalServiceValueItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [teamFilter, setTeamFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"active" | "inactive" | "all">(
    "active"
  );

  const clearFilters = () => {
    setTeamFilter("");
    setStatusFilter("active");
    setCurrentPage(1);
  };

  const hasFilterChanges = teamFilter !== "" || statusFilter !== "active";
  const [teams, setTeams] = useState<Option[]>([]);
  const [services, setServices] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<ProfessionalServiceValueItem | null>(
    null
  );
  const [toDelete, setToDelete] =
    useState<ProfessionalServiceValueItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadOptions = async () => {
    if (!entityId) return;
    try {
      const memberTeams = await professionalServiceValueService.getTeams(
        professionalId
      );
      setTeams(memberTeams.map((t) => ({ id: t.id, name: t.name })));
    } catch (error) {
      console.error("Erro ao carregar equipes:", error);
    }
    try {
      const svc = await serviceTypeService.getByEntity({
        entityId,
        professionalType: professionalTypeId || undefined,
        active: true,
        pageNumber: 1,
        pageSize: 100,
      });
      setServices(
        // O serviço de convênio leva o plano no rótulo: a mesma descrição se repete um
        // por plano, e sem isso não há como saber qual linha se está precificando (RN-221).
        svc.items.map((s) => ({ id: s.id, name: serviceTypeLabel(s) }))
      );
    } catch (error) {
      console.error("Erro ao carregar serviços:", error);
    }
  };

  const loadValues = async () => {
    if (!professionalId) return;
    setLoading(true);
    try {
      const data = await professionalServiceValueService.getByProfessional({
        professionalRegistration: professionalId,
        professionalTeam: teamFilter || undefined,
        active: statusFilter === "all" ? undefined : statusFilter === "active",
        pageNumber: currentPage,
        pageSize: itemsPerPage,
      });
      setItems(data.items);
      setTotalCount(data.totalCount);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      console.error("Erro ao carregar valores:", error);
      showToast("Erro ao carregar os valores de serviço.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = `${professionalName} / Valores de serviços`;
    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, teamFilter, statusFilter]);

  const goBack = () =>
    navigateTo("entity-users", { entityId, entityName });

  const openCreate = () => {
    setEditing(null);
    setIsModalOpen(true);
  };

  const openEdit = (item: ProfessionalServiceValueItem) => {
    setEditing(item);
    setIsModalOpen(true);
  };

  const handleSaved = () => {
    showToast("Valor de serviço salvo com sucesso.", "success");
    loadValues();
  };

  const handleToggleStatus = async (item: ProfessionalServiceValueItem) => {
    if (!item.isActive && item.hasActiveDuplicate) {
      showToast(BLOCKED_ACTIVATION_MESSAGE, "error");
      return;
    }
    try {
      await professionalServiceValueService.setStatus(item.id, !item.isActive);
      loadValues();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao alterar o status.";
      showToast(message, "error");
    }
  };

  const handleDelete = async () => {
    if (!toDelete || deleting) return;
    setDeleting(true);
    try {
      const res = await professionalServiceValueService.remove(toDelete.id);
      showToast(
        res.hardDeleted
          ? "Valor de serviço excluído com sucesso."
          : "Possui agendamentos: o valor foi inativado em vez de excluído.",
        res.hardDeleted ? "success" : "info"
      );
      setToDelete(null);
      if (res.hardDeleted && items.length === 1 && currentPage > 1) {
        setCurrentPage((p) => p - 1);
      } else {
        loadValues();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      showToast("Erro ao excluir o valor de serviço.", "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <div className="c4u-page-header c4u-page-header--in-panel">
            <IconButton
              className="c4u-page-header__back"
              onClick={goBack}
              title="Voltar para Usuários"
              size="small"
            >
              <ArrowBack />
            </IconButton>
            <div className="c4u-page-header__texts">
              <h1 className="c4u-page-header__title">{entityName} / Valores de serviços</h1>
              <p className="c4u-page-header__subtitle">Valor e repasse de cada serviço do profissional, por equipe.</p>
            </div>
            <div className="c4u-page-header__actions">
              <FaqButton />
            </div>
          </div>

          <div className="c4u-panel">
            {/* O profissional é o ESCOPO da tela: vem antes dos filtros, que recortam dentro dele.
                Não vai no título, que apresenta a tela (decisão do Hilton, 19/09/2026). */}
            <div className="c4u-scope-line">
              <strong>{professionalName}</strong>
              {especialidade ? ` · ${especialidade}` : ""}
            </div>
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
              <TextField
                select
                size="small"
                label="Filtrar por equipe"
                value={teamFilter}
                onChange={(e) => {
                  setTeamFilter(e.target.value);
                  setCurrentPage(1);
                }}
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Todas</MenuItem>
                {teams.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(
                    e.target.value as "active" | "inactive" | "all"
                  );
                  setCurrentPage(1);
                }}
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="active">Ativo</MenuItem>
                <MenuItem value="inactive">Inativo</MenuItem>
                <MenuItem value="all">Todos</MenuItem>
              </TextField>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                <AddButton onClick={openCreate} title="Adicionar valor de serviço" />
              </div>
            </div>

          <ResultCount shown={totalCount} />
          <Box className="admin-plans-list-container" sx={{ background: "none", border: "none", borderRadius: 0, boxShadow: "none" }}>
            <Box className="admin-plans-table admin-entities-table">
              <Box
                className="c4u-table__header">
                <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 160px", textAlign: "left" }}>Equipe</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "1 1 auto", minWidth: 180, textAlign: "left" }}>Serviço</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 110px", textAlign: "left" }}>Vlr. serviço</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 110px", textAlign: "left" }}>Vlr. Prof.</Box>
                <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 90px", textAlign: "left" }}>Sessão</Box>
                <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 100px", textAlign: "left" }}>Rec. indep.</Box>
                <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "0 0 95px", textAlign: "left" }}>Criação</Box>
                <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "0 0 95px", textAlign: "left" }}>Alteração</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 100px", textAlign: "left" }}>Status</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 90px", justifyContent: "flex-end" }}>Ações</Box>
              </Box>

              <Box className="admin-plans-table-body">
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
                    <CircularProgress sx={{ color: colors.primary }} />
                  </Box>
                ) : items.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: "center", color: colors.textSecondary }}>
                    Nenhum valor de serviço cadastrado para este filtro.
                  </Box>
                ) : (
                  items.map((item) => (
                    <Box
                      key={item.id}
                      className="c4u-table__row"
                    >
                      <Box className="admin-plans-cell c4u-col--md" sx={{ flex: "0 0 160px", textAlign: "left" }} data-label="Equipe">
                        {item.professionalTeamName || "-"}
                      </Box>
                      <Box className="admin-plans-cell" sx={{ flex: "1 1 auto", minWidth: 180, textAlign: "left", fontWeight: 600, whiteSpace: "normal", wordBreak: "break-word" }} data-label="Serviço">
                        {item.serviceDescription || "-"}
                      </Box>
                      <Box className="admin-plans-cell" sx={{ flex: "0 0 110px", textAlign: "left" }} data-label="Vlr. serviço">
                        {fmtMoney(item.serviceValue)}
                      </Box>
                      <Box className="admin-plans-cell" sx={{ flex: "0 0 110px", textAlign: "left" }} data-label="Vlr. Prof.">
                        {fmtMoney(item.professionalPaymentVal)}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--lg" sx={{ flex: "0 0 90px", textAlign: "left" }} data-label="Sessão">
                        {(item.sessionTimeMinutes ?? 0)} min.
                      </Box>
                      <Box className="admin-plans-cell c4u-col--lg" sx={{ flex: "0 0 100px", textAlign: "left" }} data-label="Rec. indep.">
                        {item.professionalPaymentInc ? "sim" : "não"}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--xl" sx={{ flex: "0 0 95px", textAlign: "left" }} data-label="Criação">
                        {formatDate(item.createdAt)}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--xl" sx={{ flex: "0 0 95px", textAlign: "left" }} data-label="Alteração">
                        {formatDate(item.updatedAt)}
                      </Box>
                      <Box className="admin-plans-cell" sx={{ flex: "0 0 100px", textAlign: "left" }} data-label="Status">
                        <Chip
                          label={item.isActive ? "Ativo" : "Inativo"}
                          size="small"
                          onClick={() => handleToggleStatus(item)}
                          title={
                            item.hasActiveDuplicate
                              ? BLOCKED_ACTIVATION_MESSAGE
                              : item.isActive
                                ? "Clique para inativar"
                                : "Clique para ativar"
                          }
                          sx={{
                            color: "white",
                            fontWeight: 600,
                            cursor: item.hasActiveDuplicate ? "not-allowed" : "pointer",
                            opacity: item.hasActiveDuplicate ? 0.55 : 1,
                            backgroundColor: item.isActive ? "#28a745" : "#adb5bd",
                          }}
                        />
                      </Box>
                      <Box className="admin-plans-cell admin-plans-actions" sx={{ flex: "0 0 90px", textAlign: "right" }} data-label="Ações">
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                          <Tooltip
                            title={
                              item.inUse
                                ? "Visualizar (possui agendamento, não editável)"
                                : "Editar"
                            }
                          >
                            <IconButton
                              className="c4u-row-action"
                              size="small"
                              onClick={(e) => { e.stopPropagation(); openEdit(item); }}
                              sx={{
                                backgroundColor: "#03B4C6",
                                color: "white",
                                "&:hover": { backgroundColor: "#029AAB" },
                              }}
                            >
                              <Edit sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              item.inUse
                                ? "Possui agendamento vinculado: não pode excluir"
                                : "Excluir"
                            }
                          >
                            <span>
                              <IconButton
                                className="c4u-row-action"
                                size="small"
                                onClick={(e) => { e.stopPropagation(); setToDelete(item); }}
                                disabled={item.inUse}
                                sx={{
                                  backgroundColor: "#dc3545",
                                  color: "white",
                                  "&:hover": { backgroundColor: "#c82333" },
                                  "&.Mui-disabled": {
                                    backgroundColor: "#e9a3aa",
                                    color: "white",
                                  },
                                }}
                              >
                                <Delete sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>

            {totalCount > 0 && (
              <StandardPagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalCount}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(n) => {
                  setItemsPerPage(n);
                  setCurrentPage(1);
                }}
              />
            )}
          </Box>
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />

      <ProfessionalServiceValueModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        professionalRegistration={professionalId}
        professionalName={professionalName}
        especialidade={especialidade}
        teams={teams}
        services={services}
        value={editing}
        readOnly={!!editing?.inUse}
        onSaved={handleSaved}
      />

      <ConfirmModal
        isOpen={!!toDelete}
        title="Excluir Valor de Serviço"
        message={`Deseja excluir o valor do serviço "${
          toDelete?.serviceDescription || ""
        }"? Se houver agendamentos vinculados, ele será apenas inativado.`}
        confirmButtonText="Excluir"
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
        type="danger"
        isProcessing={deleting}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Box>
  );
};

export default ProfessionalServiceValues;
